import { type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  AUTH_ERROR_MESSAGES,
  DEFAULT_SESSION_DURATION_MINUTES,
} from '@/constants';
import { Button, TextField, Typography } from '@/ui';

import { getStytchB2BHeadlessClient } from '@/lib/stytchB2B';
import {
  useGetAuthConfigQuery,
  useHandoffStytchSessionMutation,
  useLoginUserMutation,
} from '@/store/api/authApi';
import { getAuthErrorType } from '@/utils/authError';
import {
  clearStoredPostAuthRedirect,
  normalizePostAuthRedirect,
  readPostAuthRedirect,
} from '@/utils/authRedirect';
import { captureMessage } from '@/utils/errorMonitoring';
import { ensureSessionTimeout } from '@/utils/stytchSessionDuration';
import { identify, identifyEmail } from '@/utils/tracking';

const GENERIC_RESET_ERROR = 'Unable to reset password. Please try again.';

// Stytch rejects the reset with a 400 for a password below the project's
// strength policy, for an expired or used link, and for a link this browser
// cannot complete (PKCE). Tell the member which one.
// A PKCE rejection comes from another browser or device, or from an older
// email in the same browser, and the error doesn't say which, so the message
// names no cause.
const UNUSABLE_LINK_ERRORS = new Set([
  'invalid_pkce_code_verifier',
  'pkce_expected_code_verifier',
  'pkce_mismatch',
]);

async function describeResetFailure(
  resetError: unknown,
  password: string,
): Promise<ReactNode> {
  const errorType = getAuthErrorType(resetError);
  if (errorType === 'magic_link_not_found') {
    return (
      <>
        This reset link has expired or was already used. Please{' '}
        <Link className="underline" to="/signin">
          request a new reset link
        </Link>
        .
      </>
    );
  }
  if (errorType && UNUSABLE_LINK_ERRORS.has(errorType)) {
    captureMessage('password_reset_pkce_rejected', {
      extra: { error_type: errorType },
      level: 'warning',
    });
    return (
      <>
        This reset link can&apos;t be used here. Please{' '}
        <Link className="underline" to="/signin">
          request a new reset link
        </Link>
        , then open the newest email on this device.
      </>
    );
  }
  if (errorType !== 'weak_password' && errorType !== 'breached_password') {
    return GENERIC_RESET_ERROR;
  }

  // The reset error carries no feedback, so ask Stytch why the password failed.
  // The project's policy (LUDS or zxcvbn) is set in Stytch, so follow the
  // check's strength_policy rather than assuming one.
  let hints: string[] = [];
  try {
    const check = await getStytchB2BHeadlessClient().passwords.strengthCheck({
      password,
    });
    if (check.breached_password) {
      return 'That password has appeared in a known data breach. Please choose a different one.';
    }
    if (check.strength_policy === 'luds') {
      const { missing_characters: chars, missing_complexity: types } =
        check.luds_feedback;
      if (chars > 0) {
        hints.push(
          `Add at least ${chars} more character${chars === 1 ? '' : 's'}.`,
        );
      }
      if (types > 0) {
        hints.push(
          `Add ${types} more type${types === 1 ? '' : 's'} of character: uppercase, lowercase, number, or symbol.`,
        );
      }
    } else {
      hints = [
        check.zxcvbn_feedback.warning,
        ...check.zxcvbn_feedback.suggestions,
      ];
    }
  } catch {
    hints = [
      'Try a longer password with a mix of letters, numbers, and symbols.',
    ];
  }
  hints = hints.filter(Boolean);
  return (
    <>
      That password doesn&apos;t meet the password requirements.
      {hints.length > 0 ? (
        <ul className="mt-2 list-none p-0">
          {hints.map((hint) => (
            <li key={hint}>{hint}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

const Reset = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<ReactNode>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: authConfig,
    isLoading: isAuthConfigLoading,
    isError: isAuthConfigError,
  } = useGetAuthConfigQuery();
  const [handoffStytchSession] = useHandoffStytchSessionMutation();
  const [loginUser] = useLoginUserMutation();
  const redirect = search.get('redirect') ?? readPostAuthRedirect();
  const finalRedirect = normalizePostAuthRedirect(redirect);
  const authConfigReady = !isAuthConfigLoading;
  const b2bLoginEnabled =
    !isAuthConfigError && authConfig?.b2bLoginEnabled === true;

  const token = search.get('token');
  // Cross-org (Discovery) links carry stytch_token_type=discovery, but so does
  // the reset email's "log in without resetting" magic link. Like the Stytch
  // SDK (RedirectURLType.ResetPassword), only stytch_redirect_type=reset_password
  // marks a password reset. The org-scoped reset (multi_tenant_passwords, or no
  // type on older links) keeps the original path.
  const isDiscoveryLink = search.get('stytch_token_type') === 'discovery';
  const isDiscoveryReset =
    isDiscoveryLink && search.get('stytch_redirect_type') === 'reset_password';
  // Magic links are disabled, so a discovery link that is not a reset cannot
  // be completed here.
  const isUnsupportedDiscoveryLink = isDiscoveryLink && !isDiscoveryReset;

  useEffect(() => {
    if (!isUnsupportedDiscoveryLink) return;
    const params = new URLSearchParams({
      error:
        AUTH_ERROR_MESSAGES.sign_in_link_unsupported ??
        'This sign-in link is no longer supported. Please sign in with your password or Google/Microsoft.',
    });
    if (redirect) params.set('redirect', redirect);
    navigate(`/signin?${params.toString()}`, { replace: true });
  }, [isUnsupportedDiscoveryLink, navigate, redirect]);

  async function handleDiscoveryReset(resetToken: string) {
    let passwordResetSucceeded = false;
    try {
      setError(null);
      setIsSubmitting(true);
      // Discovery reset yields an intermediate session, not a member session;
      // Rails admission picks the organization and sets the session cookies.
      const response =
        await getStytchB2BHeadlessClient().passwords.discovery.resetByEmail({
          password,
          password_reset_token: resetToken,
        });
      passwordResetSucceeded = true;
      const intermediateSessionToken = response.intermediate_session_token;
      if (!intermediateSessionToken) {
        throw new Error('missing_intermediate_session_token');
      }
      if (response.email_address) {
        identifyEmail(response.email_address);
      }
      const params = new URLSearchParams({
        stytch_token_type: 'intermediate_session',
        token: intermediateSessionToken,
      });
      if (redirect) params.set('redirect', redirect);
      navigate(`/auth/discovery-callback?${params.toString()}`, {
        replace: true,
      });
    } catch (resetError) {
      if (passwordResetSucceeded) {
        // The reset token is consumed; retrying the same link cannot work.
        const params = new URLSearchParams({
          error:
            AUTH_ERROR_MESSAGES.password_reset_succeeded_signin_required ??
            'Your password was reset. Please sign in to continue.',
        });
        if (redirect) params.set('redirect', redirect);
        navigate(`/signin?${params.toString()}`, { replace: true });
        return;
      }
      setError(await describeResetFailure(resetError, password));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleB2BReset() {
    if (!token || isSubmitting) return;
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords must match.');
      return;
    }
    if (isDiscoveryReset) {
      await handleDiscoveryReset(token);
      return;
    }

    let passwordResetSucceeded = false;
    try {
      setError(null);
      setIsSubmitting(true);
      const stytch = getStytchB2BHeadlessClient();
      const handoffSession = async (session: {
        session_jwt?: string;
        session_token?: string;
      }) => {
        if (session.session_jwt) {
          await handoffStytchSession({
            sessionJwt: session.session_jwt,
          }).unwrap();
        } else if (session.session_token) {
          await handoffStytchSession({
            sessionToken: session.session_token,
          }).unwrap();
        }
      };
      const response = await stytch.passwords.resetByEmail({
        password,
        password_reset_token: token,
        session_duration_minutes: DEFAULT_SESSION_DURATION_MINUTES,
      });
      passwordResetSucceeded = true;
      await handoffSession(response);

      const email = response.member.email_address;
      const memberId = response.member.member_id;
      const memberName = response.member.name as {
        first_name?: string;
        last_name?: string;
      };
      if (!email || !memberId) {
        throw new Error('missing_b2b_member');
      }

      identifyEmail(email);
      const user = await loginUser({
        emails: [{ email }],
        name: {
          first_name: memberName.first_name ?? '',
          last_name: memberName.last_name ?? '',
        },
        providers: [],
        stytch_token_type: 'password_reset',
        user_id: memberId,
      }).unwrap();
      identify(user);
      await ensureSessionTimeout(stytch.session, user, handoffSession);
      clearStoredPostAuthRedirect();
      navigate(finalRedirect, { replace: true });
    } catch (resetError) {
      const partnerSessionExpired =
        getAuthErrorType(resetError) === 'partner_session_expired';
      if (partnerSessionExpired || passwordResetSucceeded) {
        try {
          await getStytchB2BHeadlessClient().session.revoke();
        } catch {
          // Sign-in recovery must continue even if the failed session cannot be revoked.
        }
        const params = new URLSearchParams({
          error: partnerSessionExpired
            ? (AUTH_ERROR_MESSAGES.partner_session_expired ??
              'Your session has expired. Please sign in again.')
            : (AUTH_ERROR_MESSAGES.password_reset_succeeded_signin_required ??
              'Your password was reset. Please sign in to continue.'),
        });
        if (redirect) params.set('redirect', redirect);
        navigate(`/signin?${params.toString()}`, { replace: true });
        return;
      }
      setError(await describeResetFailure(resetError, password));
    } finally {
      setIsSubmitting(false);
    }
  }

  let resetContent: ReactNode = null;
  if (!authConfigReady) {
    resetContent = null;
  } else if (isUnsupportedDiscoveryLink) {
    resetContent = null;
  } else if (token && b2bLoginEnabled) {
    resetContent = (
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
        <Typography
          className="mb-4 text-center"
          component="h1"
          fontWeight={700}
          variant="h6"
        >
          Reset your password
        </Typography>
        <TextField
          label="New password"
          margin="normal"
          type="password"
          value={password}
          fullWidth
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          label="Confirm password"
          margin="normal"
          type="password"
          value={confirmPassword}
          fullWidth
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        {error ? (
          <Typography
            className="mt-2 text-center text-red-600"
            component="div"
            variant="body2"
          >
            {error}
          </Typography>
        ) : null}
        <Button
          className="mt-4 w-full"
          disabled={isSubmitting}
          variant="contained"
          onClick={handleB2BReset}
        >
          {isSubmitting ? 'Resetting...' : 'Reset password'}
        </Button>
      </div>
    );
  } else if (token) {
    resetContent = (
      <div className="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-lg">
        <Typography variant="h6">Password reset is unavailable.</Typography>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen"
      style={{
        backgroundImage:
          'url("https://mento-space.nyc3.cdn.digitaloceanspaces.com/signin-member-bg.png")',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <div className="w-full flex-1 md:flex md:h-full">
        <div className="flex w-full flex-1 items-center justify-center">
          {resetContent}
        </div>
      </div>
    </div>
  );
};

export default Reset;
